import React, { useEffect } from 'react';
import { ADMIN_PATH, PortalProvider, STAFF_PATH, STAFF_SETUP_PATH, usePortal } from './contexts/PortalContext';
import { Login } from './pages/Login';
import { StaffLogin } from './pages/StaffLogin';
import { StaffSetupPassword } from './pages/StaffSetupPassword';
import { ClientApp } from './pages/ClientApp';
import { PortalApp } from './pages/PortalApp';
import { ViewToggle } from './components/ViewToggle';
import { PreviewBanner } from './components/PreviewBanner';
import { Toast } from './components/Toast';
import { CLIENT_ACCESS_TITLE, STAFF_TITLE, projectTitle, setPageTitle } from './utils/pageTitle';
function Portal() {
  const {
    path,
    navigate,
    staffAuthed,
    staffChecking,
    authed,
    previewAsClient,
    activeProject
  } = usePortal();
  const onAdmin = path.startsWith(ADMIN_PATH);
  /* Checked first: an invitation link must never fall through to sign-in. */
  const onStaffSetup = path.startsWith(STAFF_SETUP_PATH);
  const onStaffLogin = !onStaffSetup && path.startsWith(STAFF_PATH);

  /* Staff pages carry the Portal name; a client tab carries their own, taken
     from the stored client_name once the project has loaded. */
  useEffect(() => {
    const staffSide = onAdmin && !previewAsClient || onStaffLogin || onStaffSetup;
    if (staffSide) {
      setPageTitle(STAFF_TITLE);
      return;
    }
    if (!authed && !previewAsClient) {
      setPageTitle(CLIENT_ACCESS_TITLE);
      return;
    }
    setPageTitle(projectTitle(activeProject.client));
  }, [onAdmin, onStaffLogin, onStaffSetup, previewAsClient, authed, activeProject.client]);

  // /admin is protected: without a verified Supabase staff session you are sent
  // to /staff, and knowing the URL is never enough. Signed in, /staff hands you
  // on to /admin. Both wait for the persisted session to be checked first.
  useEffect(() => {
    if (staffChecking) return;
    if (onAdmin && !staffAuthed) navigate(STAFF_PATH);
    if (onStaffLogin && staffAuthed) navigate(ADMIN_PATH);
  }, [onAdmin, onStaffLogin, staffAuthed, staffChecking, navigate]);

  /* An invitation link: the session it carries is read here, so no redirect
     and no session check runs first. */
  if (onStaffSetup) {
    return <>
        <StaffSetupPassword data-id="element-0" data-mp="d3cuxpqygsfi" />
        <Toast data-id="element-1" data-mp="d2l8p5j9071w" />
      </>;
  }
  if (onAdmin) {
    if (staffChecking) return <div className="min-h-screen w-full bg-ink" data-id="element-2" data-mp="864afiic4cb3" />;
    if (!staffAuthed) {
      return <>
          <StaffLogin data-id="element-3" data-mp="3glwg68b7uy0t" />
          <Toast data-id="element-4" data-mp="3gj1s86szuv2n" />
        </>;
    }
    if (previewAsClient) {
      return <>
          <PreviewBanner data-id="element-5" data-mp="3bkgq21u6m1bk" />
          <ClientApp data-id="element-6" data-mp="3qbpnuy88mszs" />
          <Toast data-id="element-7" data-mp="3qc95edtz7fda" />
        </>;
    }
    return <>
        <PortalApp data-id="element-8" data-mp="3lkr6rwffdqvh" />
        <ViewToggle data-id="element-9" data-mp="3lk7lc5qkgkcp" />
        <Toast data-id="element-10" data-mp="3ljpjmtl4xdxe" />
      </>;
  }
  if (onStaffLogin) {
    /* Nothing renders until the persisted session has been checked, so an
       already-signed-in staff member never sees the login form flash. */
    if (staffChecking) return <div className="min-h-screen w-full bg-ink" data-id="element-11" data-mp="2stpjf81kwp0v" />;
    return <>
        <StaffLogin data-id="element-12" data-mp="18c6qeaqww4i2" />
        <ViewToggle data-id="element-13" data-mp="18bomcry0clzl" />
        <Toast data-id="element-14" data-mp="1d47wtg1ef106" />
      </>;
  }
  if (!authed) {
    return <>
        <Login data-id="element-15" data-mp="1d6aw6qofy6hk" />
        <ViewToggle data-id="element-16" data-mp="yflf57oatx5s" />
      </>;
  }
  return <>
      <ClientApp data-id="element-17" data-mp="yh6a2yeizi46" />
      <ViewToggle data-id="element-18" data-mp="ydil8ck0zjvs" />
      <Toast data-id="element-19" data-mp="13eof4dpf9d6w" />
    </>;
}
export function App() {
  return <PortalProvider data-id="element-20" data-mp="13ey541agvkcl">
      <Portal data-id="element-21" data-mp="13cbuttw6yqqo" />
    </PortalProvider>;
}